import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Login from "./components/Login";
import Layout from "./components/Layout";
import Dashboard from "./components/Dashboard";
import Users from "./components/Users";
import Customers from "./components/Customers";
import Products from "./components/Products";
import Orders from "./components/Orders";
import AbandonedCheckouts from "./components/AbandonedCheckouts";
import Reservations from "./components/Reservations";
import Categories from "./components/Categories";
import Brands from "./components/Brands";
import Colors from "./components/Colors";
import Conditions from "./components/Conditions";
import Locations from "./components/Locations";
import Currencies from "./components/Currencies";
import ShippingRates from "./components/ShippingRates";
import Countries from "./components/Countries";
import HomeBanner from "./components/HomeBanner";
import Blogs from "./components/Blogs";
import StyleCollections from "./components/StyleCollections";
import ProductSettings from "./components/ProductSettings";
import MobileVersionSettings from "./components/MobileVersionSettings";
import PushNotifications from "./components/PushNotifications";
import Closet from "./components/Closet";
import Permissions from "./components/Permissions";
import Settings from "./components/Settings";
import MailSettings from "./components/MailSettings";
import OrderDetailPage from "./pages/OrderDetailPage";
import AbandonedCheckoutDetailPage from "./pages/AbandonedCheckoutDetailPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import ProductEditPage from "./pages/ProductEditPage";
import CategoryFormPage from "./pages/CategoryFormPage";
import BlogFormPage from "./pages/BlogFormPage";
import BrandFormPage from "./pages/BrandFormPage";
import ColorFormPage from "./pages/ColorFormPage";
import ConditionFormPage from "./pages/ConditionFormPage";
import LocationFormPage from "./pages/LocationFormPage";
import CurrencyFormPage from "./pages/CurrencyFormPage";
import ShippingRateFormPage from "./pages/ShippingRateFormPage";
import CountryEditPage from "./pages/CountryEditPage";
import UserFormPage from "./pages/UserFormPage";
import CustomerEditPage from "./pages/CustomerEditPage";
import PermissionManagePage from "./pages/PermissionManagePage";
import ClosetProductsPage from "./pages/ClosetProductsPage";
import ClosetInfluencerFormPage from "./pages/ClosetInfluencerFormPage";
import InfluencerBankDetailsPage from "./pages/InfluencerBankDetailsPage";
import ReservationDetailPage from "./pages/ReservationDetailPage";
import StyleCollectionFormPage from "./pages/StyleCollectionFormPage";
import Auctions from "./components/Auctions";
import AuctionFormPage from "./pages/AuctionFormPage";
import AuctionDepositsAdminPage from "./pages/AuctionDepositsAdminPage";
import AuctionBidsPage from "./pages/AuctionBidsPage";
import AuctionLivePage from "./pages/AuctionLivePage";
import AuctionResultsPage from "./pages/AuctionResultsPage";
import "./App.css";
import { isInfluencerUser } from "./utils/auth";
import { productSettingsAPI, authAPI, clearAdminSession } from "./services/api";

// Protected Route Component
const ProtectedRoute = ({ children, isAuthenticated, authReady }) => {
  if (!authReady) {
    return <div className="loading">Loading…</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Paths that influencer accounts are not allowed to access
const INFLUENCER_RESTRICTED_PATHS = [
  "/users",
  "/customers",
  "/colors",
  "/conditions",
  "/locations",
  "/currencies",
  "/countries",
  "/shipping-rates",
  "/brands",
  "/home-banner",
  "/blogs",
  "/style-collections",
  "/auctions",
  "/auction-deposits",
  "/product-settings",
  "/mobile-app-version",
  "/notifications",
  "/closet",
  "/permissions",
  "/abandoned-checkouts",
  "/categories",
  "/settings",
  "/mail-settings",
];

const AuctionsEnabledGuard = ({ children }) => {
  const [enabled, setEnabled] = React.useState(true);
  const [ready, setReady] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    productSettingsAPI
      .get()
      .then((res) => {
        if (!cancelled) {
          setEnabled(res.data?.auctions_enabled !== false);
          setReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEnabled(true);
          setReady(true);
        }
      });
    const onChange = () => {
      productSettingsAPI
        .get()
        .then((res) => setEnabled(res.data?.auctions_enabled !== false))
        .catch(() => {});
    };
    window.addEventListener('exglam-settings-changed', onChange);
    return () => {
      cancelled = true;
      window.removeEventListener('exglam-settings-changed', onChange);
    };
  }, []);

  if (!ready) return <div className="loading">Loading…</div>;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return children;
};

const InfluencerRouteGuard = ({ children }) => {
  const location = useLocation();
  const isInfluencer = isInfluencerUser();
  const isRestricted = INFLUENCER_RESTRICTED_PATHS.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`),
  );
  if (isInfluencer && isRestricted) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Main App Routes
const AppRoutes = ({ onLogout }) => {
  return (
    <Layout onLogout={onLogout}>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/orders/:orderId" element={<OrderDetailPage />} />
        <Route
          path="/reservations/:reservationId"
          element={<ReservationDetailPage />}
        />
        <Route path="/reservations" element={<Reservations />} />
        <Route path="/products/new" element={<ProductEditPage />} />
        <Route path="/products/:productId/edit" element={<ProductEditPage />} />
        <Route path="/products/:productId" element={<ProductDetailPage />} />
        <Route path="/bank-details" element={<InfluencerBankDetailsPage />} />
        <Route path="/products" element={<Products />} />
        <Route path="/categories/new" element={<CategoryFormPage />} />
        <Route
          path="/categories/:categoryId/edit"
          element={<CategoryFormPage />}
        />
        <Route path="/categories" element={<Categories />} />
        <Route path="/brands/new" element={<BrandFormPage />} />
        <Route path="/brands/:brandId/edit" element={<BrandFormPage />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/colors/new" element={<ColorFormPage />} />
        <Route path="/colors/:colorId/edit" element={<ColorFormPage />} />
        <Route path="/colors" element={<Colors />} />
        <Route
          path="/conditions/:conditionId/edit"
          element={<ConditionFormPage />}
        />
        <Route path="/conditions" element={<Conditions />} />
        <Route path="/locations/new" element={<LocationFormPage />} />
        <Route
          path="/locations/:locationId/edit"
          element={<LocationFormPage />}
        />
        <Route path="/locations" element={<Locations />} />
        <Route path="/currencies/new" element={<CurrencyFormPage />} />
        <Route
          path="/currencies/:currencyId/edit"
          element={<CurrencyFormPage />}
        />
        <Route path="/currencies" element={<Currencies />} />
        <Route path="/shipping-rates/new" element={<ShippingRateFormPage />} />
        <Route
          path="/shipping-rates/:rateId/edit"
          element={<ShippingRateFormPage />}
        />
        <Route path="/shipping-rates" element={<ShippingRates />} />
        <Route
          path="/countries/:countryId/edit"
          element={<CountryEditPage />}
        />
        <Route path="/countries" element={<Countries />} />
        <Route path="/users/new" element={<UserFormPage />} />
        <Route path="/users/:userId/edit" element={<UserFormPage />} />
        <Route path="/users" element={<Users />} />
        <Route
          path="/customers/:customerId/edit"
          element={<CustomerEditPage />}
        />
        <Route path="/customers" element={<Customers />} />
        <Route path="/permissions/manage" element={<PermissionManagePage />} />
        <Route path="/permissions" element={<Permissions />} />
        <Route
          path="/closet/influencers/new"
          element={<ClosetInfluencerFormPage />}
        />
        <Route
          path="/closet/influencers/:influencerId/edit"
          element={<ClosetInfluencerFormPage />}
        />
        <Route
          path="/closet/influencers/:influencerId/products"
          element={<ClosetProductsPage />}
        />
        <Route path="/closet" element={<Closet />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/abandoned-checkouts" element={<AbandonedCheckouts />} />
        <Route
          path="/abandoned-checkouts/:checkoutId"
          element={<AbandonedCheckoutDetailPage />}
        />
        <Route path="/blogs/new" element={<BlogFormPage />} />
        <Route path="/blogs/:blogId/edit" element={<BlogFormPage />} />
        <Route path="/blogs" element={<Blogs />} />
        <Route
          path="/style-collections/new"
          element={<StyleCollectionFormPage />}
        />
        <Route
          path="/style-collections/:collectionId/edit"
          element={<StyleCollectionFormPage />}
        />
        <Route path="/style-collections" element={<StyleCollections />} />
        <Route
          path="/auction-deposits"
          element={
            <AuctionsEnabledGuard>
              <AuctionDepositsAdminPage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions/new"
          element={
            <AuctionsEnabledGuard>
              <AuctionFormPage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions/:auctionId/edit"
          element={
            <AuctionsEnabledGuard>
              <AuctionFormPage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions/:auctionId/bids"
          element={
            <AuctionsEnabledGuard>
              <AuctionBidsPage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions/:auctionId/live"
          element={
            <AuctionsEnabledGuard>
              <AuctionLivePage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions/:auctionId/results"
          element={
            <AuctionsEnabledGuard>
              <AuctionResultsPage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions/:auctionId"
          element={
            <AuctionsEnabledGuard>
              <AuctionFormPage />
            </AuctionsEnabledGuard>
          }
        />
        <Route
          path="/auctions"
          element={
            <AuctionsEnabledGuard>
              <Auctions />
            </AuctionsEnabledGuard>
          }
        />
        <Route path="/settings" element={<Settings />} />
        <Route path="/mail-settings" element={<MailSettings />} />
        <Route path="/home-banner" element={<HomeBanner />} />
        <Route path="/notifications" element={<PushNotifications />} />
        <Route path="/mobile-app-version" element={<MobileVersionSettings />} />
        <Route path="/product-settings" element={<ProductSettings />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Layout>
  );
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      setAuthReady(true);
      return;
    }

    authAPI
      .me()
      .then((response) => {
        const user = response.data?.user;
        if (user) {
          localStorage.setItem("admin_user", JSON.stringify(user));
        }
        setIsAuthenticated(true);
      })
      .catch(() => {
        clearAdminSession();
        setIsAuthenticated(false);
      })
      .finally(() => {
        setAuthReady(true);
      });
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    clearAdminSession();
    setIsAuthenticated(false);
  };

  return (
    <Router>
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              authReady={authReady}
            >
              <InfluencerRouteGuard>
                <AppRoutes onLogout={handleLogout} />
              </InfluencerRouteGuard>
            </ProtectedRoute>
          }
        />
      </Routes>
      <ToastContainer
        position="top-right"
        autoClose={3200}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastStyle={{
          borderRadius: "12px",
          boxShadow: "0 8px 28px rgba(28, 20, 25, 0.12)",
          border: "1px solid #ebe6ea",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "14px",
        }}
        theme="light"
      />
    </Router>
  );
}

export default App;
